import React, { useMemo, useState } from 'react';
import useRecipientEstimate from '../hooks/useRecipientEstimate';
import { buildFilterPayload, parseManualRecipients, mergeAndDedupeEmails, countDuplicatesAndInvalid } from '../utils/recipientFilterHelpers';
import { importRecipientsCsv, validateRecipients } from '../../../../services/emailRecipientService';
import { useNotification } from '../../../../components/framework/NotificationContext';

// type values must match RecipientFilterRequest.CriterionType exactly (backend enum names).
// Labels use consistent, compact phrasing so the two-column grid stays aligned.
const FILTER_TYPES = [
    { type: 'ALL_USERS', label: 'All users', icon: 'bi-people' },
    { type: 'VERIFIED', label: 'Verified users', icon: 'bi-patch-check' },
    { type: 'UNVERIFIED', label: 'Unverified users', icon: 'bi-patch-exclamation' },
    { type: 'CONTRIBUTORS', label: 'Contributors', icon: 'bi-pencil-square' },
    { type: 'MODERATORS', label: 'Moderators', icon: 'bi-shield-check' },
    { type: 'ADMINISTRATORS', label: 'Administrators', icon: 'bi-shield-lock' },
    { type: 'PENDING_SUBMISSIONS', label: 'Pending submissions', icon: 'bi-hourglass-split' },
    { type: 'REJECTED_SUBMISSIONS', label: 'Rejected submissions', icon: 'bi-x-circle' },
    { type: 'NEEDS_UPDATE_SUBMISSIONS', label: 'Needs-update submissions', icon: 'bi-arrow-repeat' },
    { type: 'NEVER_SUBMITTED', label: 'Never submitted', icon: 'bi-inbox' },
    { type: 'HAS_ORCID', label: 'Has ORCID on file', icon: 'bi-person-vcard' },
    { type: 'MISSING_ORCID', label: 'Missing ORCID', icon: 'bi-person-x' },
];

/**
 * Recipient targeting: manual entry, CSV import, dynamic role/status filters and a live
 * estimated/actual/duplicate/invalid recipient count, per the Email Center Phase 1 spec.
 */
const RecipientsPanel = ({ onRecipientsChange }) => {
    const { error: notifyError } = useNotification();
    const [logic, setLogic] = useState('OR');
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [dateRange, setDateRange] = useState({ from: '', to: '' });
    const [manualText, setManualText] = useState('');
    const [csvEmails, setCsvEmails] = useState([]);
    const [csvFileName, setCsvFileName] = useState('');
    const [validation, setValidation] = useState(null);
    const [validating, setValidating] = useState(false);

    const toggleFilter = (type) => {
        setSelectedFilters((prev) =>
            prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
        );
    };

    // Flat list of CriterionType names; REGISTERED_DATE_RANGE is added whenever either
    // bound is set (its actual from/to values travel separately as top-level fields).
    const criteria = useMemo(() => {
        const list = [...selectedFilters];
        if ((dateRange.from || dateRange.to) && !list.includes('REGISTERED_DATE_RANGE')) {
            list.push('REGISTERED_DATE_RANGE');
        }
        return list;
    }, [selectedFilters, dateRange]);

    const manualEmails = useMemo(() => parseManualRecipients(manualText), [manualText]);

    // The backend's RecipientFilterRequest only has one `manualEmails` list - CSV-imported
    // addresses are merged into it here rather than sent as a separate top-level field.
    const filterPayload = useMemo(
        () =>
            buildFilterPayload(
                { logic, criteria, registeredFrom: dateRange.from || null, registeredTo: dateRange.to || null },
                [...manualEmails, ...csvEmails]
            ),
        [logic, criteria, manualEmails, csvEmails, dateRange]
    );

    const { estimatedCount, loading: estimating } = useRecipientEstimate(filterPayload);

    const mergedPreview = useMemo(
        () =>
            mergeAndDedupeEmails(
                { emails: manualEmails, label: 'MANUAL' },
                { emails: csvEmails, label: 'CSV' }
            ),
        [manualEmails, csvEmails]
    );
    const dupInvalid = useMemo(() => countDuplicatesAndInvalid(mergedPreview), [mergedPreview]);

    React.useEffect(() => {
        onRecipientsChange?.({ filterPayload, estimatedCount, manualEmails, csvEmails });
    }, [JSON.stringify(filterPayload), estimatedCount]);

    const handleCsvUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setCsvFileName(file.name);
        try {
            const result = await importRecipientsCsv(file);
            setCsvEmails(result?.emails || []);
        } catch (err) {
            notifyError(err.message || 'Failed to import CSV file');
        }
    };

    const handleValidate = async () => {
        setValidating(true);
        try {
            const result = await validateRecipients(filterPayload);
            setValidation(result);
        } catch (err) {
            notifyError(err.message || 'Failed to validate recipients');
        } finally {
            setValidating(false);
        }
    };

    return (
        <div className="admin-card mb-3">
            <h5 className="mb-3"><i className="bi bi-people me-2"></i>Recipients</h5>

            <div className="mb-3">
                <label className="form-label fw-semibold">Manual recipients</label>
                <textarea
                    className="form-control"
                    rows={2}
                    placeholder="one@example.com, two@example.com"
                    value={manualText}
                    onChange={(e) => setManualText(e.target.value)}
                />
            </div>

            <div className="mb-3">
                <label className="form-label fw-semibold">Import CSV</label>
                <input type="file" accept=".csv" className="form-control" onChange={handleCsvUpload} />
                {csvFileName && (
                    <div className="form-text">
                        {csvFileName}: {csvEmails.length} address{csvEmails.length === 1 ? '' : 'es'} imported
                    </div>
                )}
            </div>

            <div className="mb-2 d-flex justify-content-between align-items-center">
                <label className="form-label fw-semibold mb-0">Dynamic recipient filters</label>
                <select className="form-select form-select-sm" style={{ width: 'auto' }} value={logic} onChange={(e) => setLogic(e.target.value)}>
                    <option value="OR">Match ANY selected (OR)</option>
                    <option value="AND">Match ALL selected (AND)</option>
                </select>
            </div>
            <div className="email-filter-grid mb-2">
                {FILTER_TYPES.map((f) => {
                    const checked = selectedFilters.includes(f.type);
                    return (
                        <label
                            key={f.type}
                            htmlFor={`filter-${f.type}`}
                            className={`email-filter-chip${checked ? ' checked' : ''}`}
                        >
                            <input
                                className="form-check-input me-2"
                                type="checkbox"
                                id={`filter-${f.type}`}
                                checked={checked}
                                onChange={() => toggleFilter(f.type)}
                            />
                            <i className={`bi ${f.icon} me-2 text-muted`}></i>
                            <span>{f.label}</span>
                        </label>
                    );
                })}
            </div>
            <div className="row g-2 mb-3">
                <div className="col-md-6">
                    <label className="form-label small">Registered from</label>
                    <input type="date" className="form-control form-control-sm" value={dateRange.from} onChange={(e) => setDateRange((p) => ({ ...p, from: e.target.value }))} />
                </div>
                <div className="col-md-6">
                    <label className="form-label small">Registered to</label>
                    <input type="date" className="form-control form-control-sm" value={dateRange.to} onChange={(e) => setDateRange((p) => ({ ...p, to: e.target.value }))} />
                </div>
            </div>

            <div className="d-flex justify-content-between align-items-center border-top pt-3">
                <div>
                    <div className="fw-semibold">
                        Estimated recipients: {estimating ? '…' : (estimatedCount ?? 0)}
                    </div>
                    {validation && (
                        <div className="small text-muted">
                            Actual: {validation.validCount} · Duplicates: {validation.duplicateCount} · Invalid: {validation.invalidCount}
                        </div>
                    )}
                    {!validation && (dupInvalid.duplicates > 0 || dupInvalid.invalid > 0) && (
                        <div className="small text-warning">
                            {dupInvalid.duplicates} duplicate(s), {dupInvalid.invalid} invalid address(es) detected locally
                        </div>
                    )}
                </div>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleValidate} disabled={validating}>
                    {validating ? 'Validating…' : 'Validate Recipients'}
                </button>
            </div>
        </div>
    );
};

export default RecipientsPanel;
